/**
 * Display helpers for the citizen inbox (F50) — how a message is grouped,
 * previewed and linked on screen, on the web AND the phone (one copy, so the
 * two group and preview a message identically). Pure: every decision about the message itself
 * (what it says, whether it is read, whether its copy was retained) comes from
 * the server; these only shape it for reading.
 */
import type { InboxItem } from '@tonnie/types';

// ── Day groups ───────────────────────────────────────────────────────────────

export type InboxDayGroup = 'today' | 'yesterday' | 'thisWeek' | 'earlier';

const DAY_MS = 86_400_000;

function startOfLocalDay(date: Date): number {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
}

/**
 * Which calendar-day bucket a message falls in, in the reader's own time zone
 * — "yesterday" means the previous calendar day, not "24 hours ago".
 */
export function inboxDayGroup(sentAt: string, now: Date): InboxDayGroup {
  const days = Math.round((startOfLocalDay(now) - startOfLocalDay(new Date(sentAt))) / DAY_MS);
  if (days <= 0) return 'today';
  if (days === 1) return 'yesterday';
  if (days < 7) return 'thisWeek';
  return 'earlier';
}

export interface InboxDaySection {
  group: InboxDayGroup;
  items: InboxItem[];
}

/**
 * Consecutive runs of the (newest-first) list, one section per day bucket. The
 * server's order is kept exactly: a section never reorders or merges rows.
 */
export function groupInboxByDay(items: readonly InboxItem[], now: Date): InboxDaySection[] {
  const sections: InboxDaySection[] = [];
  for (const item of items) {
    const group = inboxDayGroup(item.sentAt, now);
    const last = sections[sections.length - 1];
    if (last && last.group === group) last.items.push(item);
    else sections.push({ group, items: [item] });
  }
  return sections;
}

/** True when the message arrived on the reader's current calendar day. */
export function isToday(sentAt: string, now: Date): boolean {
  return inboxDayGroup(sentAt, now) === 'today';
}

// ── Preview text ─────────────────────────────────────────────────────────────

const PREVIEW_MAX = 160;

/** One line of plain text: whitespace collapsed, clipped on a word boundary. */
export function previewLine(text: string, max = PREVIEW_MAX): string {
  const flat = text.replace(/\s+/g, ' ').trim();
  if (flat.length <= max) return flat;
  const cut = flat.slice(0, max);
  const lastSpace = cut.lastIndexOf(' ');
  return `${(lastSpace > max * 0.6 ? cut.slice(0, lastSpace) : cut).trimEnd()}…`;
}

// ── Sender initials ──────────────────────────────────────────────────────────

/** Up to two initials for a company tile: "Acme Relocation" → "AR". */
export function senderInitials(name: string): string {
  const words = name
    .split(/[\s._+@-]+/)
    .map((w) => w.replace(/[^\p{L}\p{N}]/gu, ''))
    .filter(Boolean);
  const initials = words
    .slice(0, 2)
    .map((w) => (Array.from(w)[0] ?? '').toUpperCase())
    .join('');
  return initials || '?';
}

// ── Links in plain text ──────────────────────────────────────────────────────

export type TextSegment = { kind: 'text'; value: string } | { kind: 'link'; value: string };

/** http(s) URLs only — never `javascript:`, `data:` or any other scheme. */
const URL_PATTERN = /\bhttps?:\/\/[^\s<>"']+/gi;
/** Sentence punctuation that ends a URL written in prose rather than belonging to it. */
const TRAILING_PUNCTUATION = /[.,;:!?)\]}'"]+$/;

/**
 * Split an email's plain text into text and link segments, so the reader can
 * make its links clickable without ever rendering the tenant's HTML.
 */
export function splitLinks(text: string): TextSegment[] {
  const segments: TextSegment[] = [];
  let cursor = 0;
  for (const match of text.matchAll(URL_PATTERN)) {
    const start = match.index ?? 0;
    const raw = match[0];
    const trailing = raw.match(TRAILING_PUNCTUATION)?.[0] ?? '';
    const url = trailing ? raw.slice(0, raw.length - trailing.length) : raw;
    if (start > cursor) segments.push({ kind: 'text', value: text.slice(cursor, start) });
    segments.push({ kind: 'link', value: url });
    cursor = start + url.length;
  }
  if (cursor < text.length) segments.push({ kind: 'text', value: text.slice(cursor) });
  return segments;
}
